import React, { useContext, useEffect, useState } from "react";

import { ZeosApiContext } from "../../context/ZeosApiContext";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import {
  IMeteringAttestationContract,
  IServiceAttestationAnswer,
  IServiceProfileContract,
  METERING_ATTESTATION_STATUS,
  METERING_PROFILE_OPTIONS,
  profileTakesMeterCount,
} from "../../services/zeos-api/contracts/Property";
import { ZeosButton } from "../Shared/ZeosButton";
import { ZeosModal } from "../Shared/ZeosModal";
import { ZeosRadioGroup } from "../Shared/ZeosRadioGroup";
import { LinkAnotherMeter } from "./LinkAnotherMeter";

// MTR-P0-04 migration attestation (task-4048, spec v0.3). Self-contained: fetches the caller's
// pending properties and asks, one property at a time, how each of its services (electric,
// gas, ...) is metered. Only services not yet answered are asked; each resolves on its own, so
// "Not sure" for gas does not hold up a known electric answer. The API only returns properties
// in companies where the caller is company Admin, so once every service is answered a property
// is never shown again. "Remind me later" (or closing the modal) hides the prompt for the rest
// of the browser session. Mounted on Portfolio (OQ-7); it can be moved as-is.

export const METERING_ATTESTATION_DISMISSED_KEY = "zeos.meteringAttestation.dismissed";

const isSuccess = (status?: string) => status?.toLowerCase() === "success";

const labelFor = (value: string) =>
  METERING_PROFILE_OPTIONS.find((o) => o.value === value)?.label ?? "";

const valueFor = (label: string) =>
  METERING_PROFILE_OPTIONS.find((o) => o.label === label)?.value ?? "";

interface IServiceDraft {
  profile: string;
  count: string; // raw input; blank = not known yet
}

// Blank, or a whole number of at least 1.
const isValidCount = (count: string) => count.trim() === "" || /^[1-9]\d*$/.test(count.trim());

const servicesToAsk = (property?: IMeteringAttestationContract): IServiceProfileContract[] =>
  property?.services?.filter(
    (s) => s.attestationStatus === METERING_ATTESTATION_STATUS.AwaitingAnswer
  ) ?? [];

// R8 / task-4061: services the answer says have more meters than ZEOS has linked.
interface IShortfall {
  serviceName: string;
  expected: number;
  linked: number;
}

const shortfallsFor = (
  services: IServiceProfileContract[],
  answers: IServiceAttestationAnswer[]
): IShortfall[] =>
  answers.flatMap((answer) => {
    const service = services.find((s) => s.utilityTypeId === answer.utilityTypeId);
    const expected = answer.expectedMeterCount ?? 0;
    return service && expected > service.liveMeterCount
      ? [{ serviceName: service.serviceName, expected, linked: service.liveMeterCount }]
      : [];
  });

export const MeteringAttestationPrompt = () => {
  const api = useContext(ZeosApiContext);
  const [dismissed, setDismissed] = useSessionStorage(
    METERING_ATTESTATION_DISMISSED_KEY,
    false
  );
  const [pending, setPending] = useState<IMeteringAttestationContract[]>([]);
  const [drafts, setDrafts] = useState<Record<number, IServiceDraft>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  // After a save that expects more meters than are linked: offer to link them before moving on.
  const [shortfalls, setShortfalls] = useState<IShortfall[]>([]);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (dismissed) {
      return;
    }
    let cancelled = false;
    const loadPending = async () => {
      try {
        const response = await api.Property.meteringAttestationPendingAsync();
        if (!cancelled && isSuccess(response.zeosStatus) && Array.isArray(response.data)) {
          setPending(response.data.filter((p) => servicesToAsk(p).length > 0));
        }
      } catch {
        // Non-blocking prompt: if the list cannot be loaded, simply do not show it.
      }
    };
    loadPending();
    return () => {
      cancelled = true;
    };
    // Load once per mount; dismissing only ever hides it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = pending[0];
  const services = servicesToAsk(current);
  const isOpen = !dismissed && Boolean(current);

  const draftFor = (utilityTypeId: number): IServiceDraft =>
    drafts[utilityTypeId] ?? { profile: "", count: "" };

  const updateDraft = (utilityTypeId: number, change: Partial<IServiceDraft>) => {
    setDrafts((all) => ({ ...all, [utilityTypeId]: { ...draftFor(utilityTypeId), ...change } }));
  };

  const isComplete = services.every((s) => {
    const draft = draftFor(s.utilityTypeId);
    return draft.profile !== "" && (!profileTakesMeterCount(draft.profile) || isValidCount(draft.count));
  });

  const handleRemindLater = () => {
    setDismissed(() => true);
  };

  const advance = () => {
    setShortfalls([]);
    setLinking(false);
    setPending((list) => list.slice(1));
    setDrafts({});
  };

  const handleSave = async () => {
    if (!current || !isComplete) {
      return;
    }
    const answers: IServiceAttestationAnswer[] = services.map((s) => {
      const draft = draftFor(s.utilityTypeId);
      const count = profileTakesMeterCount(draft.profile) ? draft.count.trim() : "";
      return {
        utilityTypeId: s.utilityTypeId,
        meteringProfile: draft.profile,
        expectedMeterCount: count === "" ? null : Number(count),
      };
    });

    setIsSaving(true);
    setError("");
    try {
      const response = await api.Property.submitMeteringAttestationAsync(current.propertyId, answers);
      if (isSuccess(response.zeosStatus)) {
        const missing = shortfallsFor(services, answers);
        if (missing.length > 0) {
          setShortfalls(missing);
        } else {
          advance();
        }
      } else {
        setError(response.message || "We couldn't save your answer. Please try again.");
      }
    } catch {
      setError("We couldn't save your answer. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!current) {
    return null;
  }

  const location = [current.address, current.city, current.state]
    .filter(Boolean)
    .join(", ");

  if (shortfalls.length > 0) {
    return (
      <ZeosModal isOpen={isOpen} onClose={handleRemindLater}>
        <div className="pr-6">
          {linking ? (
            <LinkAnotherMeter
              propertyId={current.propertyId}
              propertyName={current.propertyName}
              onFinished={advance}
            />
          ) : (
            <>
              <h3 className="text-xl font-bold text-zeos-black mb-1">Link the other meters?</h3>
              <p className="text-zeos-black mb-3">
                <strong>{current.propertyName}</strong>
              </p>
              <ul className="text-sm text-gray-700 mb-3 list-disc ml-5">
                {shortfalls.map((s) => (
                  <li key={s.serviceName}>
                    You told us about {s.expected} {s.serviceName.toLowerCase()} meters; ZEOS has {s.linked} linked.
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-600 mb-4">
                Building totals stay incomplete until every meter is linked. You can link another utility account now,
                or later from the property's Meters.
              </p>
              <div className="flex justify-end gap-3">
                <ZeosButton label="Not now" type="button" mode="prev" onPress={advance} />
                <ZeosButton label="Link another meter" type="button" mode="action" onPress={() => setLinking(true)} />
              </div>
            </>
          )}
        </div>
      </ZeosModal>
    );
  }

  return (
    <ZeosModal isOpen={isOpen} onClose={handleRemindLater}>
      <div className="pr-6">
        <h3 className="text-xl font-bold text-zeos-black mb-1">
          How is this property metered?
        </h3>
        {pending.length > 1 && (
          <p className="text-sm text-gray-500 mb-3">
            1 of {pending.length} properties to confirm
          </p>
        )}
        <p className="text-zeos-black mb-1">
          <strong>{current.propertyName}</strong> ({current.companyName})
        </p>
        {location && <p className="text-sm text-gray-600 mb-1">{location}</p>}
        <p className="text-sm text-gray-600 mb-4">
          For each utility, tell us who holds the accounts. Your answers tell us whether
          building totals are complete.
        </p>

        {services.map((service) => {
          const draft = draftFor(service.utilityTypeId);
          const countId = `meter-count-${service.utilityTypeId}`;
          const countInvalid = profileTakesMeterCount(draft.profile) && !isValidCount(draft.count);
          return (
            <fieldset
              key={service.utilityTypeId}
              aria-label={service.serviceName}
              className="border border-gray-200 rounded p-3 mb-3"
            >
              <legend className="px-1 font-semibold text-zeos-black">{service.serviceName}</legend>
              <p className="text-xs text-gray-500 mb-2">
                ZEOS currently has {service.liveMeterCount} {service.serviceName.toLowerCase()} meter
                {service.liveMeterCount === 1 ? "" : "s"} linked.
              </p>
              <ZeosRadioGroup
                name={`meteringProfile-${service.utilityTypeId}`}
                options={METERING_PROFILE_OPTIONS.map((o) => o.label)}
                value={labelFor(draft.profile)}
                onChange={(label) => updateDraft(service.utilityTypeId, { profile: valueFor(label) })}
                disabled={isSaving}
              />
              {profileTakesMeterCount(draft.profile) && (
                <div className="mt-2">
                  <label htmlFor={countId} className="block text-sm text-zeos-black mb-1">
                    How many {service.serviceName.toLowerCase()} meters? (optional)
                  </label>
                  <input
                    id={countId}
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                    value={draft.count}
                    onChange={(e) => updateDraft(service.utilityTypeId, { count: e.target.value })}
                    disabled={isSaving}
                    aria-invalid={countInvalid}
                  />
                  {countInvalid && (
                    <p className="text-xs text-red-600 mt-1">Enter a whole number of 1 or more, or leave it blank.</p>
                  )}
                </div>
              )}
            </fieldset>
          );
        })}

        {error && (
          <p role="alert" className="text-red-600 text-sm mt-2">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <ZeosButton
            label="Remind me later"
            type="button"
            mode="prev"
            onPress={handleRemindLater}
            isDisabled={isSaving}
          />
          <ZeosButton
            label={isSaving ? "Saving..." : "Save"}
            type="button"
            mode="action"
            onPress={handleSave}
            isDisabled={isSaving || !isComplete}
          />
        </div>
      </div>
    </ZeosModal>
  );
};
